import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Select from "react-select";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext, useAuth } from '../context/AuthContext';
import { useLocationContext } from "../context/LocationContext";
import useApi from '../hooks/useApi';

const RegisterModal = ({
  show,
  onClose,
  onLoginClick,
  isFromProfile = false,
  onEnrollSuccess,
  isSchoolAdmin = false,
  initialCourseId = "",
  lockBodyScroll = true,
  enrollHeading = "Enroll in Course",
  enrollDescription = "Select your course and schedule",
  navigation,
  schoolId
}) => {
  const navigate = useNavigate();
  const { loginUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    zipcode: "",
    dob: "",
    gender: "",
    agree: false,
  });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(isFromProfile ? "enroll" : "register");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId ? String(initialCourseId) : "");
  const [selectedCourseDuration, setSelectedCourseDuration] = useState(0);
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [assignSchool, setAssignSchool] = useState(null);
  const { user, viewMode, setViewMode, saveAuthToken } = useAuth();
  const { bearerAuth, publicAuth } = useApi();

  // OTP State
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [registeredUserData, setRegisteredUserData] = useState(null);

  // New states for city and school
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [schools, setSchools] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSchoolOption, setSelectedSchoolOption] = useState(null);
  const [isCityManual, setIsCityManual] = useState(false);
  const { location: currentLocation, requestLocation } = useLocationContext();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const markerIconRef = useRef(null);

  // Enrollment For options
  const [enrollmentFor, setEnrollmentFor] = useState("self");
  const enrollmentOptions = [
    { value: "self", label: "Self" },
    { value: "parent", label: "Parent" },
    { value: "other", label: "Other" }
  ];
  const [schoolPreferences, setSchoolPreferences] = useState({
    femaleDriverOnly: false,
    nearbyOnly: false,
    minPreferenceRating: "",
    preferredCarType: "",
  });

  useEffect(() => {
    if (show) {
      if (isFromProfile) {
        if (user) {
          setFormData({
            name: user.name || "",
            email: user.email || "",
            password: "",
            phone: "",
            zipcode: "",
            dob: "",
            gender: "",
            agree: true,
          });
          // Remove auto school assignment from user data
          setAssignSchool(null);
        }
      } else {
        resetForm();
      }
      // fetchCourses();
      fetchCities();
      requestLocation();
      if (lockBodyScroll) {
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = "15px";
      } else {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show, isFromProfile, isSchoolAdmin, lockBodyScroll]);

  useEffect(() => {
    if (
      schoolPreferences.nearbyOnly &&
      currentLocation?.latitude != null &&
      currentLocation?.longitude != null &&
      !isCityManual
    ) {
      fetchNearbySchools(currentLocation.latitude, currentLocation.longitude);
      return;
    }

    if (selectedCity) {
      fetchSchoolsForCity(selectedCity.id);
    }
  }, [selectedCity, currentLocation, isCityManual, schoolPreferences]);

  useEffect(() => {
    if (!show || step !== "enroll") return;
    initEnrollMap();
  }, [show, step]);

  useEffect(() => {
    if (!show || step !== "enroll") return;
    updateEnrollMapMarkers();
  }, [schools, show, step]);

  useEffect(() => {
    if (!mapRef.current || !selectedSchoolOption) return;
    const school = selectedSchoolOption.school;
    const lat = parseFloat(school?.latitude);
    const lng = parseFloat(school?.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    mapRef.current.setView([lat, lng], 14);
  }, [selectedSchoolOption]);

  useEffect(() => {
    if (!selectedSchoolOption) return;
    const stillExists = schools.some((s) => String(s.key) === String(selectedSchoolOption.value));
    if (!stillExists) {
      setSelectedSchoolOption(null);
      setSelectedSchool(null);
      setAssignSchool(null);
    }
  }, [schools, selectedSchoolOption]);

  useEffect(() => {
    if (selectedCourseId && selectedCourseDuration > 0) {
      calculateDateRange();
    }
  }, [selectedCourseId, selectedCourseDuration, includeWeekends, selectedDate]);

  // Resend OTP Timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Fetch cities
  const fetchCities = async () => {
    try {
      const res = await publicAuth("cities", "GET");
      const list = res?.data?.data || res?.data?.cities || [];
      setCities(list);
      const stateList = Array.from(
        new Set(list.map((c) => (c.state_name || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      setStates(stateList);
    } catch (e) {
      console.error("cities fetch error:", e);
      setCities([]);
    }
  };

  // Fetch schools for selected city
  const fetchSchoolsForCity = async (cityId) => {
    if (!cityId) {
      setSchools([]);
      return;
    }
    try {
      const res = await publicAuth("driving-schools", "POST", {
        city_id: cityId,
        female_driver_only: schoolPreferences.femaleDriverOnly,
        preferred_car_type: schoolPreferences.preferredCarType || undefined,
        min_preference_rating: schoolPreferences.minPreferenceRating || undefined,
      });
      const list = res?.data?.data || res?.data?.schools || [];
      setSchools(list);
    } catch (e) {
      console.error("schools fetch error:", e);
      setSchools([]);
    }
  };

  const fetchNearbySchools = async (lat, lng) => {
    try {
      const res = await publicAuth("driving-schools/nearby", "GET", {
        lat,
        lng,
        radius: 10,
        female_driver_only: schoolPreferences.femaleDriverOnly,
        preferred_car_type: schoolPreferences.preferredCarType || undefined,
        min_preference_rating: schoolPreferences.minPreferenceRating || undefined,
      });
      const list = res?.data?.data || res?.data?.schools || [];
      if (list.length > 0) {
        setSchools(list);
        if (!isCityManual) {
          const firstCityId = list[0]?.city_id;
          const matchedCity = cities.find((c) => String(c.id) === String(firstCityId)) || null;
          if (matchedCity && !selectedCity) {
            setSelectedCity(matchedCity);
            fetchCourses(matchedCity.name);
          }
        }
      } else if (selectedCity?.id) {
        fetchSchoolsForCity(selectedCity.id);
      } else {
        setSchools([]);
      }
    } catch (e) {
      console.error("nearby schools fetch error:", e);
      if (selectedCity?.id) {
        fetchSchoolsForCity(selectedCity.id);
      } else {
        setSchools([]);
      }
    }
  };

  const fetchCourses = async (city) => {
    try {
      const res = await publicAuth(`courses?city=${city}`);
      const list = res.data?.courses || res.data?.data || [];
      setCourses(list);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };

  // Check enrollment status for user
  const checkEnrollmentStatus = async (userId) => {
    try {
      const res = await bearerAuth(`myinfo`, "GET");
      if (res.data?.status && res.data.data) {
        const userData = res.data.data;
        console.log("Enrollment status check:", userData);
        return userData.enroll_status; // 'pending', 'accepted', 'rejected'
      }
      return 'pending';
    } catch (err) {
      console.error("Failed to check enrollment status:", err);
      return 'pending';
    }
  };

  const calculateDateRange = () => {
    if (!selectedCourseDuration) {
      setDateRange([]);
      return;
    }
    let daysToAdd = selectedCourseDuration;
    let date = new Date(selectedDate);
    let daysCounted = 0;
    let range = [new Date(date)];
    while (daysCounted < daysToAdd - 1) {
      date.setDate(date.getDate() + 1);
      if (includeWeekends || (date.getDay() !== 0 && date.getDay() !== 6)) {
        daysCounted++;
        range.push(new Date(date));
      }
    }
    setDateRange(range);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      zipcode: "",
      dob: "",
      gender: "",
      agree: false,
    });
    setSelectedCourseId("");
    setSelectedCourseDuration(0);
    setSelectedDate(new Date());
    setSelectedTimeSlot(null);
    setErrors({});
    setStep(isFromProfile ? "enroll" : "register");
    setShowDateTimeModal(false);
    setShowPassword(false);
    setIsLoading(false);
    setIncludeWeekends(false);
    setDateRange([]);
    setOtp("");
    setOtpError("");
    setResendTimer(0);
    setCanResend(false);
    setRegisteredUserData(null);
    // Reset city and school
    setSelectedState("");
    setSelectedCity(null);
    setSelectedSchool(null);
    setSelectedSchoolOption(null);
    setSchools([]);
    setIsCityManual(false);
    // Reset enrollment for
    setEnrollmentFor("self");
    setSchoolPreferences({
      femaleDriverOnly: false,
      nearbyOnly: false,
      minPreferenceRating: "",
      preferredCarType: "",
    });
    // Reset assigned school
    setAssignSchool(null);
  };

  // State change handler
  const onStateChange = (e) => {
    const val = e.target.value;
    setSelectedState(val);
    setSelectedCity(null);
    setSchools([]);
    setSelectedSchool(null);
    setSelectedSchoolOption(null);
    setCourses([]);
    setErrors((prev) => ({ ...prev, state: "", city: "", school: "" }));
  };

  // City change handler
  const onCityChange = (e) => {
    const val = e.target.value;
    const cityObj = cities.find((c) => String(c.id) === String(val)) || null;
    setSelectedCity(cityObj);
    setIsCityManual(true);

    if (cityObj?.state_name && !selectedState) {
      setSelectedState(cityObj.state_name);
    }

    if (cityObj) {
      fetchCourses(cityObj.name);
    } else {
      setCourses([]);
      setSchools([]);
    }
    setSelectedSchool(null);
    setSelectedSchoolOption(null);
    setAssignSchool(null); // Reset assigned school when city changes
    setErrors((prev) => ({ ...prev, city: "", school: "" }));
  };

  useEffect(() => {
    if (!currentLocation?.city) return;
    if (!cities.length) return;
    if (selectedCity) return;
    if (isCityManual) return;

    const matchedCity = cities.find(
      (c) => String(c.name).toLowerCase() === String(currentLocation.city).toLowerCase()
    );

    if (matchedCity) {
      if (matchedCity.state_name) {
        setSelectedState(matchedCity.state_name);
      }
      setSelectedCity(matchedCity);
      fetchCourses(matchedCity.name);
    }
  }, [currentLocation, cities, selectedCity, isCityManual]);

  // School change handler - UPDATED: Now directly sets assignSchool
  const onSchoolChange = (e) => {
    const option = e;
    const schoolObj = option?.school || null;
    setSelectedSchoolOption(option || null);
    setSelectedSchool(schoolObj);
    setAssignSchool(option?.value || null);
    setErrors((prev) => ({ ...prev, school: "" }));
  };

  // Enrollment For change handler
  const handleEnrollmentForChange = (e) => {
    setEnrollmentFor(e.target.value);
    setErrors((prev) => ({ ...prev, enrollmentFor: "" }));
  };

  const handlePreferenceChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "nearbyOnly" && checked) {
      setIsCityManual(false);
    }
    setSchoolPreferences((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const hasActiveSchoolFilters = Boolean(
    schoolPreferences.femaleDriverOnly ||
      schoolPreferences.nearbyOnly ||
      schoolPreferences.minPreferenceRating ||
      schoolPreferences.preferredCarType
  );

  // Fixed: Reset OTP state when going back to registration
  const handleBackToRegister = () => {
    setOtp("");
    setOtpError("");
    setStep("register");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const { name, email, password, phone, agree, zipcode } = formData;
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]+$/.test(name))
      newErrors.name = "Only letters and spaces allowed";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email";
    if (!password.trim()) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "At least 6 characters";
    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[6-9]\d{9}$/.test(phone))
      newErrors.phone = "Enter valid 10-digit Indian number";
    if (!zipcode) newErrors.zipcode = "Enter a 6-digit ZIP code";
    else if (!/^[1-9][0-9]{5}$/.test(zipcode))
      newErrors.zipcode = "Invalid Indian ZIP code";
    if (!isSchoolAdmin && !agree) newErrors.agree = "You must agree to the terms";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEnrollForm = () => {
    const newErrors = {};
    if (!selectedState) newErrors.state = "Please select a state";
    if (!selectedCity) newErrors.city = "Please select a city";
    if (!selectedSchool) newErrors.school = "Please select a driving school";
    if (!selectedCourseId) newErrors.course = "Please select a course";
    if (!enrollmentFor) newErrors.enrollmentFor = "Please select enrollment for";
    if (!formData.dob) newErrors.dob = "Please select date of birth";
    if (!formData.gender) newErrors.gender = "Please select gender";

    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }

      if (age <= 16) {
        newErrors.dob = "You must be older than 16 years to register.";
        Swal.fire({
          icon: "warning",
          title: "Not Eligible for Registration",
          text: "Based on the date of birth provided, you are currently not eligible to register. Applicants must be above 16 years of age.",
          confirmButtonColor: "#0d6efd",
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const registerUser = async (userData) => {
    try {
      const payload = {
        full_name: userData.name,
        email: userData.email,
        password: userData.password,
        contact: userData.phone,
        zipcode: userData.zipcode,
        ...(currentLocation?.latitude != null && currentLocation?.longitude != null
          ? { latitude: currentLocation.latitude, longitude: currentLocation.longitude }
          : {}),
      };
      
      // REMOVED: Auto school assignment based on viewMode or admin
      // Now school will be assigned only during enrollment from user selection
      
      if (viewMode === 'school') {
        payload.register_by = user?.id;
      }
      const res = await publicAuth(`register`, "POST", payload);
      if (res.data.status) {
        return res.data;
      }
      return res;
    } catch (err) {
      console.error("Registration error:", err);
      throw err;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const response = await registerUser(formData);
      console.log("Registration response:", response);

      setRegisteredUserData(formData);

      setResendTimer(30);
      setCanResend(false);
      setStep("otp");
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Something went wrong during registration",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return false;
    }
    setIsLoading(true);
    try {
      const res = await publicAuth(`verify-otp`, "POST", {
        email: registeredUserData.email,
        otp: otp,
      });

      if (res.data.status) {
        // FIXED: School admin aur normal user ke liye alag behavior
        if (isSchoolAdmin) {
          // School admin ke liye auto-login mat karo, bas enroll step pe jao
          await Swal.fire({
            icon: "success",
            title: "Student Created",
            text: "Student account created and verified. You can enroll them now.",
            showConfirmButton: false,
            timer: 1500,
          });
          setStep("enroll");
        } else {
          // Normal user ke liye login karo aur viewMode set karo
          loginUser(res.data.data);
          setViewMode("user");
          saveAuthToken(res.data.token || res.token);
          setStep("enroll");
        }
        return true;
      } else {
        setOtpError(res.data.message || "Invalid OTP");
        return false;
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "OTP verification failed");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    setIsLoading(true);
    try {
      await publicAuth(`resend-otp`, "POST", { email: registeredUserData.email });
      setResendTimer(30);
      setCanResend(false);
      setOtp("");
      setOtpError("");
      Swal.fire({
        icon: "success",
        title: "OTP Resent!",
        text: "A new OTP has been sent to your email.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to resend OTP", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTimeSlots = async (date) => {
    try {
      const formattedDate = date.toISOString().split("T")[0];
      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: assignSchool, // Now this uses user-selected school
      });
      console.log("Fetched time slots for school:", assignSchool, res.data);
      const slots = res.data?.slots || res.data?.data || [];
      setAvailableSlots(slots);
      setSelectedTimeSlot(null);
    } catch (err) {
      console.error("Slot fetch error:", err);
      setAvailableSlots([]);
    }
  };

  const formatSlotLabel = (slot) => {
    const base = `${slot.start_time} - ${slot.end_time}`;
    const capacity = Number(slot.capacity);
    const bookedCount = Number(slot.booked_count || 0);
    if (capacity > 0) {
      return `${base} (${bookedCount}/${capacity})${slot.booked ? " - Full" : ""}`;
    }
    return base;
  };

  const handleEnroll = async () => {
    if (!validateEnrollForm()) return;
    setIsLoading(true);
    const userId = user?.id;
    if (!userId) {
      Swal.fire("Error", "User ID not found. Please register again.", "error");
      setStep("register");
      setIsLoading(false);
      return;
    }
    try {
      const payload = {
        course_id: selectedCourseId,
        inc_sat_sun: includeWeekends,
        assignSchool: assignSchool, // This now contains user-selected school
        enrollment_for: enrollmentFor,
        dob: formData.dob,
        gender: formData.gender,
        pref_female_driver: schoolPreferences.femaleDriverOnly,
        pref_nearby_only: schoolPreferences.nearbyOnly,
        pref_min_rating: schoolPreferences.minPreferenceRating || null,
        preferred_car_type: schoolPreferences.preferredCarType || null,
        ...(currentLocation?.latitude != null && currentLocation?.longitude != null
          ? { student_latitude: currentLocation.latitude, student_longitude: currentLocation.longitude }
          : {}),
        ...(isSchoolAdmin && { registered_by_admin: true }),
      };
      if (selectedDate) {
        payload.expected_start_date = selectedDate.toISOString().split("T")[0];
      }
      if (selectedTimeSlot) {
        payload.time_slot = `${selectedTimeSlot.start_time} - ${selectedTimeSlot.end_time}`;
      }
      
      console.log("Enrollment payload with selected school:", payload);
      
      const res = await bearerAuth(`enroll`, "POST", payload);
      if (res.data.status) {
        // Check enrollment status before redirecting
        const enrollmentStatus = await checkEnrollmentStatus(userId);
        
        if (enrollmentStatus === 'accepted') {
          // If accepted, redirect to dashboard
          await Swal.fire({
            icon: "success",
            title: isSchoolAdmin ? "Student Added Successfully!" : "Enrollment Successful!",
            text: isSchoolAdmin
              ? "Student has been registered and enrolled successfully."
              : "You have been successfully enrolled in the course. Redirecting to dashboard...",
            showConfirmButton: false,
            timer: 2000,
          });
          
          // FIXED: School admin aur normal user ke liye alag redirect behavior
          if (!isSchoolAdmin) {
            // Normal user - login karo aur dashboard redirect karo
            const userData = {
              id: userId,
              assignSchool: assignSchool,
              name: formData.name,
              email: formData.email,
            };
            loginUser(userData);
            setViewMode("user"); // Ensure viewMode set hai
            window.dispatchEvent(new Event('authChange'));
            
            if (onEnrollSuccess) {
              onEnrollSuccess();
            } else {
              resetForm();
              onClose();
              navigate("/dashboard");
            }
          } else {
            // School admin - bas form reset karo aur close karo, redirect mat karo
            if (onEnrollSuccess) {
              onEnrollSuccess();
            }
            resetForm();
            onClose();
            // School admin ko redirect mat karo - wahi school dashboard pe rahe
          }
        } else {
          // If pending, show pending message and stay on page
          await Swal.fire({
            icon: "info",
            title: "Enrollment Submitted",
            html: `
              <div class="text-center">
                <p>Your enrollment has been submitted successfully!</p>
                <p><strong>Status: Pending Approval</strong></p>
                <p class="small text-muted">
                  Your enrollment is pending approval from the driving school. 
                  You will be notified once it's accepted.
                </p>
              </div>
            `,
            confirmButtonText: "OK",
            confirmButtonColor: "#0d6efd",
          });
          
          // For normal users, redirect to pending page or stay
          if (!isSchoolAdmin) {
            const userData = {
              id: userId,
              assignSchool: assignSchool,
              name: formData.name,
              email: formData.email,
            };
            loginUser(userData);
            setViewMode("user");
            window.dispatchEvent(new Event('authChange'));
            
            if (onEnrollSuccess) {
              onEnrollSuccess();
            } else {
              resetForm();
              onClose();
              // Redirect to pending page instead of dashboard
              navigate("/pending-approval");
            }
          } else {
            // School admin - just close the modal
            if (onEnrollSuccess) {
              onEnrollSuccess();
            }
            resetForm();
            onClose();
          }
        }
      } else {
        Swal.fire("Error", res.data.message || "Enrollment failed", "error");
      }
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Something went wrong during enrollment",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchTimeSlots(date);
    calculateDateRange();
  };

  const openDateTimeModal = () => {
    if (!selectedCourseId) {
      setErrors({ course: "Please select a course first" });
      return;
    }
    if (!selectedSchool) {
      setErrors({ school: "Please select a driving school first" });
      return;
    }
    if (!assignSchool) {
      setErrors({ school: "Please select a valid driving school" });
      return;
    }
    fetchTimeSlots(selectedDate);
    setShowDateTimeModal(true);
  };

  const handleCourseSelect = (e) => {
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    setErrors((prev) => ({ ...prev, course: "" }));
    if (courseId) {
      const selectedCourse = courses.find((c) => c.id === courseId);
      if (selectedCourse) {
        setSelectedCourseDuration(parseInt(selectedCourse.duration) || 0);
      }
    } else {
      setSelectedCourseDuration(0);
    }
  };

  const toggleIncludeWeekends = () => {
    setIncludeWeekends(!includeWeekends);
  };

  const renderDayContents = (day, date) => {
    const isInRange = dateRange.some((d) => d.toDateString() === date.toDateString());
    const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
    return (
      <div
        style={{
          backgroundColor: isSelected ? "#0d6efd" : isInRange ? "#e6f2ff" : "transparent",
          fontWeight: isSelected ? "bold" : "normal",
          color: isSelected ? "#fff" : isInRange ? "#0d6efd" : "inherit",
          borderRadius: "50%",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {day}
      </div>
    );
  };

  const formatSchoolLabel = (school) => {
    const uname = (school?.username || "").toString().trim();
    const score = school?.preference_rating ? ` | Rated ${school.preference_rating}` : "";
    const femaleDriver = school?.has_female_driver_vehicle ? " | Female driver" : "";
    return `${uname || "***"} ASYSTA PARTNER SCHOOL${score}${femaleDriver}`;
  };

  const getSchoolOption = (school) => ({
    value: school.key,
    label: formatSchoolLabel(school),
    school,
  });

  const initEnrollMap = () => {
    if (mapRef.current || !mapContainerRef.current) return;
    if (!window.L) return;

    const L = window.L;
    const map = L.map(mapContainerRef.current, {
      center: [20.5937, 78.9629],
      zoom: 5,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    markerLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    markerIconRef.current = L.divIcon({
      className: "school-marker-icon",
      html: '<div class="marker-badge">L</div>',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -28],
    });

    if (currentLocation?.latitude && currentLocation?.longitude) {
      map.setView([currentLocation.latitude, currentLocation.longitude], 12);
    }

    setTimeout(() => map.invalidateSize(), 100);
  };

  const updateEnrollMapMarkers = () => {
    if (!mapRef.current || !markerLayerRef.current || !window.L) return;
    const L = window.L;
    markerLayerRef.current.clearLayers();

    const markers = [];
    schools.forEach((school) => {
      const lat = parseFloat(school.latitude);
      const lng = parseFloat(school.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const marker = L.marker([lat, lng], {
        icon: markerIconRef.current || undefined,
      });
      marker.on("click", () => {
        const option = getSchoolOption(school);
        setSelectedSchoolOption(option);
        setSelectedSchool(school);
        setAssignSchool(option.value);
        setErrors((prev) => ({ ...prev, school: "" }));
      });
      marker.bindPopup(formatSchoolLabel(school), { maxWidth: 260 });
      marker.addTo(markerLayerRef.current);
      markers.push(marker);
    });

    if (markers.length > 0) {
      const group = L.featureGroup(markers);
      mapRef.current.fitBounds(group.getBounds().pad(0.2));
    }
  };

  if (!show) return null;

  return (
    <>  
     <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
          overflow-y: auto;
          padding: 20px;
        }
        .modal-box {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 15px;
          padding: 30px;
          width: 100%;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          position: relative;
          overflow: hidden;
          margin: auto;
        }
        .modal-box.modal-md {
          max-width: 500px;
        }
        .modal-box.modal-lg {
          max-width: 1000px;
        }
        .modal-box::before {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 120px;
          background-image: url('${process.env.PUBLIC_URL}/assets/images/logincar.png');
          background-size: contain;
          background-position: center bottom;
          background-repeat: no-repeat;
          opacity: 0.15;
          z-index: 0;
        }
        .registerclose {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 40px;
          height: 40px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.7rem;
          line-height: 1;
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid #dee2e6;
          border-radius: 50%;
          cursor: pointer;
          color: #6c757d;
          transition: color 0.2s;
          z-index: 10050;
          pointer-events: auto;
          touch-action: manipulation;
        }
        .registerclose:hover {
          color: #495057;
        }
        .modal-content {
          position: relative;
          z-index: 1;
        }
        .enroll-shell {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .enroll-panel {
          border-radius: 22px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 250, 255, 0.98));
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }
        .enroll-panel-body {
          padding: 16px;
        }
        .enroll-panel-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #102a43;
        }
        .enroll-panel-note {
          margin: 0;
          font-size: 0.9rem;
          color: #52606d;
        }
        .preference-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }
        .preference-card {
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 72px;
          padding: 12px 14px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid rgba(13, 110, 253, 0.08);
        }
        .preference-toggle-card {
          gap: 10px;
        }
        .preference-toggle-head {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 28px;
        }
        .preference-toggle-label {
          margin: 0;
          font-weight: 600;
          color: #102a43;
        }
        .preference-select-label {
          margin-bottom: 8px;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: #486581;
        }
        .preference-card .form-select {
          min-height: 42px;
          border-radius: 12px;
          border-color: #d9e2ec;
          background-color: #f8fbff;
        }
        .enroll-map-frame {
          height: 250px;
          width: 100%;
          border-radius: 16px;
          border: 1px solid rgba(13, 110, 253, 0.12);
          overflow: hidden;
          background: #edf4ff;
        }
        .enroll-map-note {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          color: #52606d;
          font-size: 0.84rem;
        }
        .enroll-map-note::before {
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #0d6efd;
          flex-shrink: 0;
        }
        .enroll-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          align-items: start;
        }
        .enroll-form-grid > .row:first-child {
          grid-column: 1 / -1;
          margin: 0;
        }
        .enroll-form-grid > .enroll-select-wrap,
        .enroll-form-grid > .enroll-select-wrap + .form-group,
        .enroll-form-grid > .enroll-select-wrap + .form-group + .form-group,
        .enroll-form-grid > .enroll-actions {
          margin-top: 0 !important;
        }
        .enroll-form-grid > .enroll-select-wrap {
          grid-column: 1;
        }
        .enroll-form-grid > .enroll-select-wrap + .form-group {
          grid-column: 2;
        }
        .enroll-form-grid > .enroll-select-wrap + .form-group + .form-group {
          grid-column: 1;
        }
        .enroll-form-grid > .enroll-actions {
          grid-column: 1 / -1;
          display: grid !important;
          gap: 8px;
          align-content: stretch;
          margin-top: 8px !important;
        }
        .enroll-form-grid > .enroll-actions .btn {
          width: 100%;
        }
        .enroll-form-grid .form-group {
          margin-bottom: 0;
        }
        .enroll-form-grid .form-label {
          margin-bottom: 6px;
          font-size: 0.82rem;
          font-weight: 700;
          color: #102a43;
        }
        .enroll-form-grid .form-control,
        .enroll-form-grid .form-select {
          min-height: 44px;
          border-radius: 12px;
          border-color: #d9e2ec;
          background-color: #f8fbff;
        }
        .enroll-form-grid .form-control:focus,
        .enroll-form-grid .form-select:focus {
          background-color: #ffffff;
        }
        .enroll-quick-grid {
          margin-top: 10px;
        }
        .enroll-select-wrap .react-select__control {
          min-height: 44px;
          border-radius: 12px;
          border-color: #d9e2ec;
          background-color: #f8fbff;
          box-shadow: none;
        }
        .enroll-select-wrap .react-select__control--is-focused {
          border-color: #86b7fe;
          background-color: #ffffff;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.12);
        }
        .enroll-select-wrap .react-select__value-container {
          padding: 6px 12px;
        }
        .enroll-select-wrap .react-select__menu {
          z-index: 10000;
        }
        .enroll-select-wrap .react-select__menu-portal {
          z-index: 10000;
        }
        .enroll-time-card {
            padding: 10px 12px;
            border-radius: 16px;
            background: linear-gradient(180deg, #ffffff, #f8fbff);
            border: 1px solid rgba(13, 110, 253, 0.08);
          }
        .enroll-time-trigger {
            border-radius: 999px;
            padding: 5px 12px;
            font-weight: 600;
            font-size: 0.92rem;
          }
        .enroll-selection-summary {
            margin-top: 6px;
            padding: 4px 8px;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            background: linear-gradient(180deg, rgba(13, 110, 253, 0.08), rgba(13, 110, 253, 0.03));
            border: 1px solid rgba(13, 110, 253, 0.1);
            color: #334e68;
            font-size: 0.8rem;
            line-height: 1.2;
          }
        .enroll-footer-row {
          margin-top: 10px;
          align-items: stretch;
        }
        .enroll-selection-summary strong {
          color: #102a43;
        }
        .enroll-actions {
          height: 100%;
        }
        .enroll-primary-btn,
        .enroll-secondary-btn {
            min-height: 38px;
            border-radius: 12px;
            font-weight: 700;
            letter-spacing: 0.01em;
            padding-top: 8px;
            padding-bottom: 8px;
          }
        .enroll-primary-btn {
          background: linear-gradient(135deg, #0d6efd, #4f8dfd);
          box-shadow: 0 14px 30px rgba(13, 110, 253, 0.22);
        }
        .enroll-primary-btn:hover {
          background: linear-gradient(135deg, #0b5ed7, #377dfc);
        }
        .enroll-secondary-btn {
          border-width: 1px;
        }
        .form-control {
          border-radius: 8px;
          border: 1px solid #ced4da;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-control:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .school-marker-icon {
          background: transparent;
          border: none;
        }
        .school-marker-icon .marker-badge {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #0d6efd;
          color: #ffffff;
          font-weight: 700;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 12px rgba(13, 110, 253, 0.35);
          border: 2px solid #ffffff;
        }
        .btn-primary {
          background-color: #0d6efd;
          border: none;
          border-radius: 8px;
          padding: 12px;
          font-weight: 500;
          transition: background-color 0.2s;
        }
        .btn-primary:hover {
          background-color: #0b5ed7;
        }
        .text-danger {
          font-size: 0.85rem;
          margin-top: 5px;
        }
        .input-group-text {
          border-radius: 8px 0 0 8px !important;
        }
        .password-input-group {
          position: relative;
        }
        .password-toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #6c757d;
          background: none;
          border: none;
          padding: 0;
        }
        .password-toggle:hover {
          color: #495057;
        }
        .custom-calendar {
          width: 100%;
          border: none;
          box-shadow: none;
        }
        .react-datepicker {
          font-family: inherit;
          border: none;
        }
        .react-datepicker__header {
          background-color: #f8f9fa;
          border-bottom: none;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #0d6efd;
          color: white;
        }
        .react-datepicker__day--highlight-range {
          background-color: #e6f2ff;
          color: #0d6efd;
          font-weight: normal;
        }
        .react-datepicker__day--highlight-range:hover {
          background-color: #cce0ff;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--highlight-range.react-datepicker__day--selected {
          background-color: #0d6efd;
          color: white;
          font-weight: bold;
        }
        .date-time-modal .modal-content {
            border-radius: 15px;
          }
        .date-time-modal {
            position: fixed;
            inset: 0;
            z-index: 1300;
            background-color: rgba(15, 23, 42, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
          }
        .date-time-modal .modal-box {
            width: min(820px, 100%);
            max-height: min(88vh, 760px);
            overflow-y: auto;
            margin: 0;
            padding: 22px;
          }
        .date-time-modal .card {
            border: none;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            border-radius: 10px;
          }
        .time-slots-container {
          max-height: 200px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .time-slot-btn {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-height: 44px;
          font-size: 0.9rem;
          padding: 8px 12px;
        }
        .otp-input {
          letter-spacing: 10px;
          font-size: 1.5rem;
          text-align: center;
          font-weight: bold;
        }
        .resend-link {
          color: #0d6efd;
          text-decoration: underline;
          cursor: pointer;
        }
        .resend-link:disabled, .resend-link.disabled {
          color: #6c757d;
          cursor: not-allowed;
          text-decoration: none;
        }
        @media (max-width: 768px) {
          .modal-box {
            padding: 20px;
            max-width: 95%;
          }
          .enroll-form-grid {
            display: block;
          }
          .enroll-form-grid > .enroll-select-wrap,
          .enroll-form-grid > .enroll-select-wrap + .form-group,
          .enroll-form-grid > .enroll-select-wrap + .form-group + .form-group,
          .enroll-form-grid > .enroll-actions {
            margin-top: 10px !important;
          }
          .preference-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .enroll-map-frame {
            height: 220px;
          }
          .form-control {
            padding: 10px 12px;
          }
          .btn-primary {
            padding: 10px;
          }
          .date-time-modal .modal-box {
            max-width: 95%;
          }
        }
        @media (max-width: 576px) {
          .modal-box {
            padding: 15px;
          }
          .preference-grid {
            grid-template-columns: 1fr;
          }
          .enroll-panel-body {
            padding: 16px;
          }
          .enroll-map-frame {
            height: 210px;
          }
          .enroll-time-card .d-flex {
            align-items: flex-start !important;
            flex-direction: column;
          }
          .enroll-time-trigger {
            width: 100%;
          }
          .date-time-modal .row {
            flex-direction: column;
          }
          .date-time-modal .col-md-5,
          .date-time-modal .col-md-7 {
            width: 100%;
          }
          .date-time-modal .col-md-5 {
            margin-bottom: 15px;
          }
          .time-slot-btn {
            font-size: 0.85rem;
            padding: 6px 10px;
            min-height: 40px;
          }
        }
      `}</style>   
      <div className="modal-overlay">
        <div className={`modal-box ${step === "enroll" ? "modal-lg" : "modal-md"}`}>
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="registerclose"
            aria-label="Close register modal"
          >
            &times;
          </button>
          <div className="modal-content">
            <h2 className="text-center mb-3" style={{ color: "#343a40" }}>
              {step === "otp"
                ? "Verify OTP"
                : isSchoolAdmin
                  ? "Add New Student"
                  : step === "register"
                    ? "Create Account"
                    : enrollHeading}
            </h2>
            <p className="text-center text-muted mb-4">
              {step === "otp"
                ? `Enter the 6-digit OTP sent to ${registeredUserData?.email}`
                : isSchoolAdmin
                  ? "Enter student details"
                  : step === "register"
                    ? "Join us today!"
                    : enrollDescription}
            </p>

            {/* === STEP: REGISTER === */}
            {step === "register" && (
              <form onSubmit={handleRegister}>
                <div className="form-group mb-3">
                  <label className="form-label">Full Name*</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  {errors.name && <div className="text-danger">{errors.name}</div>}
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">Email ID*</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  {errors.email && <div className="text-danger">{errors.email}</div>}
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">Password*</label>
                  <div className="password-input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={`form-control ${errors.password ? "is-invalid" : ""}`}
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isLoading}
                      style={{ paddingRight: "40px" }}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && <div className="text-danger">{errors.password}</div>}
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">Mobile Number*</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                      placeholder="Mobile Number"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      disabled={isLoading}
                    />
                  </div>
                  {errors.phone && <div className="text-danger">{errors.phone}</div>}
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">Zipcode*</label>
                  <input
                    type="text"
                    name="zipcode"
                    className={`form-control ${errors.zipcode ? "is-invalid" : ""}`}
                    placeholder="Zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    maxLength={6}
                    disabled={isLoading}
                  />
                  {errors.zipcode && <div className="text-danger">{errors.zipcode}</div>}
                </div>
                {!isSchoolAdmin && (
                  <div className="form-group form-check mb-4">
                    <input
                      type="checkbox"
                      name="agree"
                      className={`form-check-input ${errors.agree ? "is-invalid" : ""}`}
                      checked={formData.agree}
                      onChange={handleChange}
                      id="agreeCheck"
                      disabled={isLoading}
                    />
                    <label className="form-check-label" htmlFor="agreeCheck">
                      I agree to the Terms and Conditions
                    </label>
                    {errors.agree && <div className="text-danger">{errors.agree}</div>}
                  </div>
                )}
                <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {isSchoolAdmin ? "Adding Student..." : "Registering..."}
                    </>
                  ) : isSchoolAdmin ? (
                    "ADD STUDENT"
                  ) : (
                    "REGISTER"
                  )}
                </button>
                {!isSchoolAdmin && (
                  <div className="text-center mt-3">
                    <p className="mb-0">
                      Already have an account?{" "}
                      <button
                        onClick={() => {
                          resetForm();
                          onLoginClick();
                        }}
                        className="btn btn-link p-0"
                        style={{ textDecoration: "underline", color: "#0d6efd" }}
                        disabled={isLoading}
                      >
                        Login here
                      </button>
                    </p>
                  </div>
                )}
              </form>
            )}

            {/* === STEP: OTP === */}
            {step === "otp" && (
              <div>
                <div className="form-group mb-3">
                  <label className="form-label">Enter OTP</label>
                  <input
                    type="text"
                    className={`form-control text-center otp-input ${otpError ? "is-invalid" : ""}`}
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(val);
                      setOtpError("");
                    }}
                    placeholder="------"
                    maxLength={6}
                    disabled={isLoading}
                  />
                  {otpError && <div className="text-danger text-center">{otpError}</div>}
                </div>

                <div className="text-center mb-3">
                  <small>
                    Didn't receive?{" "}
                    <span
                      className={`resend-link ${!canResend || isLoading ? "disabled" : ""}`}
                      onClick={canResend && !isLoading ? resendOtp : undefined}
                    >
                      Resend OTP {resendTimer > 0 ? `in ${resendTimer}s` : ""}
                    </span>
                  </small>
                </div>

                <div className="d-flex gap-2">
                  <button
                    onClick={handleBackToRegister}
                    className="btn btn-outline-secondary flex-grow-1"
                    disabled={isLoading}
                  >
                    Back
                  </button>
                  <button
                    onClick={verifyOtp}
                    className="btn btn-primary flex-grow-1"
                    disabled={isLoading || otp.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Verifying...
                      </>
                    ) : (
                      "VERIFY OTP"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* === STEP: ENROLL === */}
            {step === "enroll" && (
              <>
                <div className="enroll-shell">
                <div className="enroll-panel">
                  <div className="enroll-panel-body">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <h6 className="enroll-panel-title">Your Preferences</h6>
                      <small className="enroll-panel-note">We&apos;ll prioritize matching schools and vehicles.</small>
                    </div>
                    <div className="preference-grid">
                      <div className="preference-card preference-toggle-card">
                        <div className="form-check form-switch preference-toggle-head">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="prefFemaleDriverOnly"
                            name="femaleDriverOnly"
                            checked={schoolPreferences.femaleDriverOnly}
                            onChange={handlePreferenceChange}
                            disabled={isLoading}
                          />
                          <label className="form-check-label preference-toggle-label" htmlFor="prefFemaleDriverOnly">
                            Only female driver
                          </label>
                        </div>
                      </div>
                      <div className="preference-card preference-toggle-card">
                        <div className="form-check form-switch preference-toggle-head">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="prefNearbyOnly"
                            name="nearbyOnly"
                            checked={schoolPreferences.nearbyOnly}
                            onChange={handlePreferenceChange}
                            disabled={isLoading}
                          />
                          <label className="form-check-label preference-toggle-label" htmlFor="prefNearbyOnly">
                            Prioritize nearby schools
                          </label>
                        </div>
                      </div>
                      <div className="preference-card">
                        <label className="preference-select-label">Minimum School Rating</label>
                        <select
                          className="form-select"
                          name="minPreferenceRating"
                          value={schoolPreferences.minPreferenceRating}
                          onChange={handlePreferenceChange}
                          disabled={isLoading}
                        >
                          <option value="">No preference</option>
                          <option value="3">3.0+</option>
                          <option value="4">4.0+</option>
                          <option value="4.5">4.5+</option>
                        </select>
                      </div>
                      <div className="preference-card">
                        <label className="preference-select-label">Preferred Car Type</label>
                        <select
                          className="form-select"
                          name="preferredCarType"
                          value={schoolPreferences.preferredCarType}
                          onChange={handlePreferenceChange}
                          disabled={isLoading}
                        >
                          <option value="">No preference</option>
                          <option value="hatchback">Hatchback</option>
                          <option value="sedan">Sedan</option>
                          <option value="suv">SUV</option>
                          <option value="automatic">Automatic</option>
                          <option value="manual">Manual</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 align-items-stretch">
                  <div className="col-xl-4 col-lg-5">
                    <div className="enroll-panel">
                      <div className="enroll-panel-body">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <label className="enroll-panel-title">Nearby Driving Schools</label>
                      <small className="enroll-panel-note">Tap a marker to match faster.</small>
                    </div>
                    <div
                      ref={mapContainerRef}
                      className="enroll-map-frame"
                    />
                    <small className="enroll-map-note">
                      Tap a marker to select a school.
                    </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-8 col-lg-7">
                    <div className="enroll-panel">
                      <div className="enroll-panel-body enroll-form-grid">
                    <div className="row g-2">
                      {/* State Dropdown */}
                      <div className="form-group col-lg-4 col-md-6">
                        <label className="form-label">Select State*</label>
                        <select
                          className={`form-select ${errors.state ? "is-invalid" : ""}`}
                          value={selectedState}
                          onChange={onStateChange}
                          disabled={isLoading}
                        >
                          <option value="">-- Select State --</option>
                          {states.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                        {errors.state && <div className="text-danger">{errors.state}</div>}
                      </div>

                      {/* City Dropdown */}
                      <div className="form-group col-lg-4 col-md-6">
                        <label className="form-label">Select City*</label>
                        <select
                          className={`form-select ${errors.city ? "is-invalid" : ""}`}
                          value={selectedCity?.id || ""}
                          onChange={onCityChange}
                          disabled={isLoading || !selectedState}
                        >
                          <option value="">-- Select City --</option>
                          {cities
                            .filter((city) => !selectedState || city.state_name === selectedState)
                            .map((city) => (
                              <option key={city.id} value={city.id}>
                                {city.name}
                              </option>
                            ))}
                        </select>
                        {errors.city && <div className="text-danger">{errors.city}</div>}
                      </div>

                      {/* Enrollment For Dropdown */}
                      <div className="form-group col-lg-4 col-md-6">
                        <label className="form-label">Enrollment For*</label>
                        <select
                          className={`form-select ${errors.enrollmentFor ? "is-invalid" : ""}`}
                          value={enrollmentFor}
                          onChange={handleEnrollmentForChange}
                          disabled={isLoading}
                        >
                          {enrollmentOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.enrollmentFor && <div className="text-danger">{errors.enrollmentFor}</div>}
                      </div>

                      <div className="form-group col-lg-4 col-md-6">
                        <label className="form-label">Date of Birth*</label>
                        <input
                          type="date"
                          name="dob"
                          className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                          value={formData.dob}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        {errors.dob && <div className="text-danger">{errors.dob}</div>}
                      </div>

                      <div className="form-group col-lg-4 col-md-6">
                        <label className="form-label">Gender*</label>
                        <select
                          name="gender"
                          className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                          value={formData.gender}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="">-- Select Gender --</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.gender && <div className="text-danger">{errors.gender}</div>}
                      </div>
                    </div>

                {/* Driving School Dropdown */}
                <div className="form-group mt-3 enroll-select-wrap">
                  <label className="form-label">Select Driving School*</label>
                    <Select
                      classNamePrefix="react-select"
                      isDisabled={isLoading}
                      value={selectedSchoolOption}
                      onChange={onSchoolChange}
                      placeholder="-- Select Driving School --"
                      options={schools.map(getSchoolOption)}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    formatOptionLabel={(option) => (
                      <div style={{ whiteSpace: "normal", lineHeight: 1.2 }}>
                        {option.label}
                      </div>
                    )}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 44,
                        borderRadius: 12,
                        backgroundColor: "#f8fbff",
                        borderColor: errors.school ? "#dc3545" : base.borderColor,
                        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.12)" : "none",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        paddingTop: 6,
                        paddingBottom: 6,
                        paddingLeft: 12,
                        paddingRight: 12,
                      }),
                      option: (base) => ({
                        ...base,
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 10000,
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 10000,
                      }),
                    }}
                  />
                  {errors.school && <div className="text-danger mt-1">{errors.school}</div>}
                  {!isLoading && schools.length === 0 && selectedCity && hasActiveSchoolFilters && (
                    <div className="alert alert-danger mt-2 mb-0" role="alert">
                      Driving schools are not available for the current filters. Please change your filters and try again.
                    </div>
                  )}
                </div>

                {/* Course Dropdown */}
                <div className="form-group mt-3">
                  <label className="form-label">Choose a Course*</label>
                  <select
                    className={`form-select ${errors.course ? "is-invalid" : ""}`}
                    value={selectedCourseId}
                    onChange={handleCourseSelect}
                    disabled={isLoading}
                  >
                    <option value="">-- Select a Course --</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title} ({course.duration} days) - ₹{course?.price?.total_price ?? "Price Not Available"}
                      </option>
                    ))}
                  </select>
                  {errors.course && <div className="text-danger">{errors.course}</div>}
                </div>

                <div className="form-group mt-3">
                  <div className="enroll-time-card">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <label className="form-label mb-0">Choose Date & Time*</label>
                      <button
                        type="button"
                        onClick={openDateTimeModal}
                        className="btn btn-outline-primary btn-sm enroll-time-trigger"
                        disabled={!selectedCourseId || !selectedSchool || isLoading}
                    >
                      Choose Slot
                    </button>
                  </div>
                  {selectedTimeSlot ? (
                    <div className="enroll-selection-summary">
                      {selectedDate.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })} | {selectedTimeSlot.start_time} - {selectedTimeSlot.end_time}
                    </div>
                  ) : null}
                  {errors.timeSlot && <div className="text-danger mt-1">{errors.timeSlot}</div>}
                  </div>
                </div>

                <div className="d-flex gap-2 enroll-actions">
                  {!isFromProfile && (
                    <button
                      onClick={() => setStep("register")}
                      className="btn btn-outline-secondary flex-grow-1 enroll-secondary-btn"
                      disabled={isLoading}
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleEnroll}
                    className="btn btn-primary flex-grow-1 enroll-primary-btn"
                    disabled={!selectedCourseId || !selectedSchool || !enrollmentFor || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Enrolling...
                      </>
                    ) : (
                      "CONFIRM ENROLLMENT"
                    )}
                  </button>
                </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* DateTime Modal */}
      {showDateTimeModal && typeof document !== "undefined" && createPortal(
        <div className="modal-overlay date-time-modal">
          <div className="modal-box" style={{ maxWidth: "800px" }}>
            <div className="modal-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Select Date & Time Slot</h4>
                <button
                  type="button"
                  className="registerclose"
                  onClick={() => setShowDateTimeModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="includeWeekends"
                  checked={includeWeekends}
                  onChange={toggleIncludeWeekends}
                />
                <label className="form-check-label" htmlFor="includeWeekends">
                  Include Saturdays & Sundays
                </label>
              </div>
              <div className="row">
                <div className="col-md-5 mb-3 mb-md-0">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">Select Date</h6>
                      <DatePicker
                        selected={selectedDate}
                        onChange={handleDateChange}
                        inline
                        className="w-100 custom-calendar"
                        renderDayContents={renderDayContents}
                        dayClassName={(date) => {
                          const isInRange = dateRange.some(
                            (d) => d.toDateString() === date.toDateString()
                          );
                          return isInRange ? "react-datepicker__day--highlight-range" : "";
                        }}
                      />
                      {selectedCourseDuration > 0 && dateRange.length > 0 && (
                        <div className="mt-3 small text-muted">
                          <p className="mb-1">
                            <strong>Course Duration:</strong> {selectedCourseDuration} days
                          </p>
                          <p className="mb-0">
                            <strong>Period:</strong> {dateRange[0].toDateString()} to{" "}
                            {dateRange[dateRange.length - 1].toDateString()}
                            {!includeWeekends && " (excluding weekends)"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">Available Time Slots</h6>
                      {availableSlots.length === 0 ? (
                        <div className="alert alert-warning">
                          No slots available for selected date
                        </div>
                      ) : (
                        <div className="time-slots-container">
                          <div className="row">
                            {availableSlots.map((slot) => (
                              <div key={slot.id} className="col-6 mb-2">
                                  <button
                                    type="button"
                                    className={`btn w-100 time-slot-btn ${
                                      selectedTimeSlot?.id === slot.id
                                        ? "btn-primary"
                                      : slot.booked
                                      ? "btn-outline-secondary disabled"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={() => !slot.booked && setSelectedTimeSlot(slot)}
                                  disabled={slot.booked}
                                >
                                  {formatSlotLabel(slot)}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowDateTimeModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (!selectedTimeSlot) {
                      setErrors((prev) => ({
                        ...prev,
                        timeSlot: "Please select a time slot",
                      }));
                      return;
                    }
                    setShowDateTimeModal(false);
                  }}
                  disabled={!selectedTimeSlot}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export default RegisterModal;
